<template>
  <div class="form-entry row row-cols-1">

    <div v-if="!formResponse">
      <div class="col form-entry__establishment-name">
        <h1>Loading your form</h1>
      </div>
      <div class="col" v-if="!errorMessage">
        <h2>loading...</h2>
        <div class="form-entry__loading">
          <Spinner colour="#242F3E"/>
        </div>
      </div>
      <div class="alert alert-danger text-center" role="alert" v-else>{{errorMessage}}</div>
    </div>

    <div v-else>
      <!--  ------------------------------- -->
      <!--  Banner image or name            -->
      <!--  ------------------------------- -->
      <div class="col form-entry__establishment-name" v-bind:class="{'form-entry__establishment-image' : hasLogo}">
        <img v-if="hasLogo" v-bind:src="logoURL"/>
        <h1 v-else>{{formResponse.booking.establishmentName}}</h1>
      </div>

      <!--  ------------------------------- -->
      <!--  Form title                      -->
      <!--  ------------------------------- -->
      <div class="col">
        <h2>{{formResponse.title}}</h2>
        <h5 v-if="formResponse.completed">This form has been completed</h5>
        <h5 v-else-if="!isFormEditable">This form is no longer editable</h5>
      </div>

      <!--  ------------------------------- -->
      <!--  Booking summary and messaging   -->
      <!--  ------------------------------- -->
      <transition name="fade">
        <div class="col" v-show="!showingForm">
          <BookingSummaryAndMessaging v-bind:formResponse="formResponse" v-bind:submitted="submitted"/>
        </div>
      </transition>

      <!--  ------------------------------- -->
      <!--  Form                            -->
      <!--  ------------------------------- -->
      <transition name="fade">
        <div class="col" v-show="showingForm">

          <!-- Guest check-in forms need to show the booking details-->
          <BookingDetails v-if="formResponse.showBookingDetails" v-bind:formResponse="formResponse" v-bind:submitted="submitted"/>

          <ValidationObserver ref="responseForm" v-slot="{ invalid }">

            <form v-on:submit.prevent="onSubmit">

              <FormSection v-for="(section, index) in formResponse.sections"
                           v-bind:section="section"
                           v-bind:guestReference="formResponse.booking.bookedBy.reference"
                           v-bind:responseReference="formResponse.reference"
                           v-bind:section-number="index"
                           v-bind:key="section.reference"
                           v-bind:answerCache="answerCache"
                           v-bind:editable="isFormEditable"></FormSection>

              <span v-if="invalid">
                Note:
                <ul>
                  <li>
                    Your <b>answers are saved</b> as you respond, but the form is incomplete.
                  </li>
                  <li>
                    Please fill in the <span v-bind:class="{'text-error' : formValidationFailed}">missing fields</span>
                    <template v-if="isArrivalToday">
                      now, as arrival is today
                    </template>
                    <template v-else>
                      ASAP to Submit this form.
                    </template>
                  </li>
                </ul>
              </span>

              <div class="alert alert-danger text-center" role="alert" v-if="errorMessage">{{errorMessage}}</div>

              <div class="form-entry__buttons">
                <button type="submit" v-on:click="messageAndClose()" v-if="invalid" class="btn btn-secondary check-in__submit">Close</button>
                <button type="submit" v-if="isFormEditable" class="btn btn-primary check-in__submit">Submit</button>
              </div>

              <div class="col mt-5 mr-1 ml-1 mb-5">
                <span class="text-note">
                  POPIA Act compliance: Please note that any personal details provided here are stored securely and will only be
                  accessible to the establishment at which you have enquired or booked.
                </span>
              </div>

            </form>

          </ValidationObserver>

        </div>

      </transition>

    </div>

    <confirm-dialog v-bind:message="'Your answers have been saved. Please return to this form using the same link, to complete it.'"
                    v-bind:callback-name="'close-form'"
                    v-bind:continueText="'Close the form'"
                    v-bind:hideCancelButton="true"
                    v-if="showConfirmCloseDialog"/>
  </div>
</template>

<script>
import Spinner from '../components/Spinner.vue'
import ConfirmDialog from '../components/ConfirmDialog.vue'
import BookingDetails from '../components/front-desk/BookingDetails.vue'
import BookingSummaryAndMessaging from '../components/BookingSummaryAndMessaging.vue'
import FormSection from '../components/FormSection.vue'
import { ValidationObserver } from 'vee-validate'

import * as dayjs from 'dayjs'
import isToday from 'dayjs/plugin/isToday'
dayjs.extend(isToday)

export default {
  name: 'Form',
  components: { Spinner, ConfirmDialog, BookingDetails, BookingSummaryAndMessaging, FormSection, ValidationObserver },
  props: {},
  data: function () {
    return {
      showingForm: false,
      formResponse: null,
      submitted: false,
      answerCache: {},
      errorMessage: null,
      formValidationFailed: false,
      showConfirmCloseDialog: false
    }
  },
  computed: {
    hasLogo: function () {
      if (this.formResponse && this.formResponse.establishment.preferences.logo) {
        return true
      }
      return false
    },
    logoURL: function () {
      if (this.hasLogo) {
        for (const image of this.formResponse.establishment.preferences.images) {
          console.log('comparing ' + image.reference + ' to ' + this.formResponse.establishment.preferences.logo)
          if (image.reference === this.formResponse.establishment.preferences.logo) {
            return image.url
          }
        }
      }
      return ''
    },
    isFormEditable: function () {
      if (this.formResponse && this.formResponse.editable) {
        return true
      }
      return false
    },
    showCheckInSummary: function () {
      return false
    },
    isArrivalToday: function () {
      return dayjs(this.formResponse.booking.arrival).isToday()
    },
    showFormOnLoad: function () {
      if (this.isFormEditable) {
        if (!this.formResponse.showBookingSummary) {
          return true
        }
      } else if (this.formResponse.sections && this.formResponse.sections.length > 0) {
        return true
      }
      return false
    }
  },
  methods: {
    loadForm: function () {
      const guestReference = this.$route.query.fr
      this.$http.get('/guest/v1/responses/' + guestReference, { withCredentials: true }).then(response => {
        this.formResponse = response.data
        this.storeAnswersInCache()
        if (this.showFormOnLoad) {
          this.showForm()
        }
      }).catch(error => {
        console.log(error)
        if (error.response) {
          // client received an error response (5xx, 4xx)
          if (error.response.status === 404) {
            this.errorMessage = 'Sorry, but we can\'t find your form!'
          } else {
            this.errorMessage = 'Sorry, but we couldn\'t load your form properly. Please try again'
          }
        } else if (error.request) {
          // client never received a response, or request never left
        } else {
          this.errorMessage = 'Sorry, we\'re having problems preparing your form. Please try again'
        }
      })
    },
    reloadForm: function () {
      this.formResponse = null
      this.loadForm()
    },
    showForm: function () {
      this.showingForm = true
    },
    storeAnswersInCache: function () {
      this.formResponse.sections.forEach((section) => {
        section.answers.forEach((answer) => this.storeAnswerInCache(answer))
      })
    },
    storeAnswerInCache: function (answer) {
      if (answer.answer !== '') {
        let values = this.answerCache[answer.question]
        if (values === undefined) {
          values = {}
          this.answerCache[answer.question] = values
        }
        values[answer.reference] = answer.answer
      }
    },
    messageAndClose: function () {
      this.showConfirmCloseDialog = true
    },
    reloadAnswer: function (reference) {
      this.$http.get('/guest/v1/guests/' + this.formResponse.booking.bookedBy.reference +
                     '/responses/' + this.formResponse.reference +
                     '/answers/' + reference).then(response => {
        const updatedAnswer = response.data
        this.answerUpdated(updatedAnswer)
        this.$root.$emit('answer-reloaded', updatedAnswer)
      }).catch(error => {
        console.log(error)
        this.errorMessage = 'The form has been updated elsewhere, so we are reloading it for you'
        this.formResponse = null
        setTimeout(() => {
          this.reloadForm()
        }, 5000)
      })
    },
    answerUpdated: function (updatedAnswer) {
      for (const section of this.formResponse.sections) {
        for (const answer of section.answers) {
          if (answer.reference === updatedAnswer.reference) {
            Object.assign(answer, updatedAnswer)
            this.storeAnswerInCache(updatedAnswer)
          }
        }
      }
    },
    closeForm: function () {
      console.log('closing')
      this.showConfirmCloseDialog = false
      this.showingForm = false
    },
    onSubmit: function () {
      this.$refs.responseForm.validate().then(success => {
        if (!success) {
          this.formValidationFailed = true

          // find the first input that failed validation and scroll to it
          const errors = Object.entries(this.$refs.responseForm.refs)
            .map(([key, value]) => ({
              key,
              value
            }))
            .filter(error => {
              if (!error || !error.value || !error.value.failedRules) return false
              return Object.keys(error.value.failedRules).length > 0
            })

          if (errors && errors.length > 0) {
            // find the parent section and make sure it's open
            for (let i = 0; i < errors.length; i++) {
              this.$refs.responseForm.refs[errors[i].key].$parent.$parent.openSection()
            }
            this.$refs.responseForm.refs[errors[0].key].$parent.$parent.openSection(() => {
              const rect = this.$refs.responseForm.refs[errors[0].key].$el.getBoundingClientRect()
              window.scrollTo(rect.x, window.pageYOffset + rect.top - 40)
            })
          }

          return
        }

        this.formValidationFailed = false
        this.showingForm = false
        this.submitted = true
        this.errorMessage = null

        this.$http.put('/guest/v1/guests/' + this.formResponse.booking.bookedBy.reference +
          '/responses/' + this.formResponse.reference + '/completed-by-guest').then(response => {
          this.formResponse = response.data
        }).catch(error => {
          if (error.response) {
            console.log(error.response.data)
            if (error.response.status === 500) {
              this.formResponse = null
              this.errorMessage = 'Sorry, but there was a problem marking your form as complete. ' +
                'We have saved your data though. Please try refreshing the page.'
            } else {
              this.showingForm = true
              this.errorMessage = error.response.data.message
            }
          } else {
            this.errorMessage = 'Sorry, but there was a problem marking your form as complete. ' +
              'We have saved your data though. Please try refreshing the page.'
          }
        })
      })
    }
  },
  mounted () {
    this.loadForm()
    this.$root.$on('response-form-updated', this.reloadForm)
    this.$root.$on('answer-updated', this.answerUpdated)
    this.$root.$on('reload-answer', this.reloadAnswer)
    this.$root.$on('close-form', this.closeForm)
  },
  beforeDestroy () {
    this.$root.$off('answer-updated', this.answerUpdated)
    this.$root.$off('reload-answer', this.reloadAnswer)
    this.$root.$off('close-form', this.closeForm)
  }
}
</script>

<style lang="scss" scoped>
  @import "../styles/base/base.scss";
  @import "../styles/components/form_entry";
</style>
